/**
 * Superadmin protocol detail: CSV import, flow/table/DAG, validation, review, publish.
 */

import { useAuth } from "@risalabs_frontend_org/auth-wrapper";
import { Navigate, useParams } from "react-router-dom";
import { ProtocolDag } from "../views/ProtocolsView/ProtocolDag";
import { ProtocolFlowPreview } from "../views/ProtocolsView/ProtocolFlowPreview";
import { ProtocolT1LiveFunnel } from "../views/ProtocolsView/ProtocolT1LiveFunnel";
import { ProtocolTable } from "../views/ProtocolsView/ProtocolTable";
import {
  ProtocolHistoryPanels,
  ProtocolSuggestionsPanel,
  ProtocolValidationPanel,
} from "./protocolDetail/ProtocolDetailPanels";
import { ProtocolDraftToolbar } from "./protocolDetail/ProtocolDraftToolbar";
import { ProtocolImportSection } from "./protocolDetail/ProtocolImportSection";
import { useProtocolDetail } from "./protocolDetail/useProtocolDetail";

/**
 * Trial detail workspace for protocol ingestion and authoring.
 *
 * @returns Protocol detail page or redirect for non-superadmins
 */
export function ProtocolDetailPage() {
  const { trialId = "" } = useParams();
  const { user } = useAuth();
  const isSuperAdmin = Boolean(user?.isSuperAdmin);
  const detail = useProtocolDetail(trialId, isSuperAdmin);

  if (!isSuperAdmin) {
    return <Navigate to="/notifications" replace />;
  }

  const {
    trial,
    bundle,
    preview,
    validation,
    suggestions,
    versions,
    audit,
    viewMode,
    setViewMode,
    selectedTiers,
    setSelectedTiers,
    focusCriterionId,
    setFocusCriterionId,
    filterText,
    setFilterText,
    error,
    busy,
    handlePreview,
    handleConfirmImport,
    handleValidate,
    handleAcceptSuggestion,
    handleSaveCriterionRationale,
    handleAddConnection,
    handleRemoveConnection,
    handleLifecycle,
    handleArchiveToggle,
  } = detail;

  return (
    <div className="mx-auto max-w-[1600px] space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            {trial?.display_name || "Protocol"}
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            {trial?.sponsor_protocol_id} · status {trial?.site_status || "…"}
            {bundle
              ? ` · draft ${bundle.draft.status} r${bundle.draft.revision}`
              : " · no draft"}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            className="rounded border border-slate-300 px-3 py-1.5 text-sm"
            disabled={busy || !trial}
            onClick={() =>
              void handleArchiveToggle(
                trial?.site_status === "archived" ? "restore" : "archive",
              )
            }
          >
            {trial?.site_status === "archived" ? "Restore" : "Archive"}
          </button>
        </div>
      </div>

      {error ? (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <ProtocolImportSection
        busy={busy}
        preview={preview}
        onPreview={handlePreview}
        onConfirm={handleConfirmImport}
      />

      {bundle ? (
        <>
          <ProtocolDraftToolbar
            busy={busy}
            viewMode={viewMode}
            filterText={filterText}
            onViewModeChange={setViewMode}
            onFilterTextChange={setFilterText}
            onValidate={handleValidate}
            onLifecycle={handleLifecycle}
          />

          {validation ? (
            <ProtocolValidationPanel
              validation={validation}
              criteria={bundle.criteria}
              busy={busy}
              onSaveRationale={handleSaveCriterionRationale}
            />
          ) : null}

          <ProtocolSuggestionsPanel
            busy={busy}
            suggestions={suggestions}
            onAccept={handleAcceptSuggestion}
          />

          {viewMode === "flow" ? (
            <ProtocolFlowPreview
              criteria={bundle.criteria}
              atoms={bundle.atoms}
              gates={bundle.gates}
              connections={bundle.connections}
              selectedTiers={selectedTiers}
              onSelectedTiersChange={setSelectedTiers}
              onOpenCriterionInTable={(criterionId) => {
                setFocusCriterionId(criterionId || null);
                setViewMode("table");
              }}
              onOpenCriterionInDag={(criterionId) => {
                setFocusCriterionId(criterionId || null);
                setViewMode("dag");
              }}
            />
          ) : null}

          {viewMode === "live" ? (
            <ProtocolT1LiveFunnel trialId={trialId} versions={versions} />
          ) : null}

          {viewMode === "table" ? (
            <ProtocolTable
              criteria={bundle.criteria}
              atoms={bundle.atoms}
              filterText={filterText}
              selectedTiers={selectedTiers}
              onSelectedTiersChange={setSelectedTiers}
              focusCriterionId={focusCriterionId}
            />
          ) : null}

          {viewMode === "dag" ? (
            <ProtocolDag
              criteria={bundle.criteria}
              atoms={bundle.atoms}
              gates={bundle.gates}
              connections={bundle.connections}
              selectedTiers={selectedTiers}
              focusCriterionId={focusCriterionId}
              busy={busy}
              onAddConnection={(connection) =>
                void handleAddConnection(connection)
              }
              onRemoveConnection={(connectionId) =>
                void handleRemoveConnection(connectionId)
              }
            />
          ) : null}
        </>
      ) : (
        <div className="rounded-lg border border-dashed border-slate-300 p-8 text-sm text-slate-500">
          Import the BNT327-06 atoms CSV to create the first draft.
        </div>
      )}

      <ProtocolHistoryPanels versions={versions} audit={audit} />
    </div>
  );
}
